import React, { useRef, useEffect, useState } from "react"
import tw from "twin.macro"
import styled from "@emotion/styled"
import {
  ShoppingCart,
  User as UserIcon,
  LogIn as LogInIcon,
  UserPlus as UserPlusIcon,
} from "react-feather"
import { useStaticQuery, graphql, Link } from "gatsby"
import Image from "gatsby-image"
import { plural, addIrregularRule } from "pluralize"

import { Menu, Badge, Search, HamburgerMenu } from "."
import { useToggle, useSnipcart, useWindowDimensions } from "../helpers"
import { HeaderQuery } from "../../graphql-types"
import { irregularPluralWords } from "../helpers"
import { Button } from "./Button"
import ContentLoader from "react-content-loader"

const Authentication = () => {
  const [hasMounted, setHasMounted] = useState(false)
  const buttonRef = useRef(null)
  const [openMenu, toggle] = useToggle()
  const snipcartState = useSnipcart()
  const { width } = useWindowDimensions()

  useEffect(() => {
    setHasMounted(true)
  }, [])

  if (!hasMounted || !snipcartState?.customer?.status) {
    return (
      <ContentLoader
        speed={2}
        width={32}
        height={32}
        viewBox="0 0 40 40"
        backgroundColor="#f3f3f3"
        foregroundColor="#dbdbdb"
      >
        <circle cx="20" cy="20" r="20" />
      </ContentLoader>
    )
  }

  const isLoggedIn = snipcartState?.customer?.status === "SignedIn"
  const isMobile = width < 768

  return (
    <>
      {isMobile && !isLoggedIn && (
        <LogIn.Wrapper>
          <LogIn.Button ref={buttonRef} onClick={toggle}>
            <UserIcon color="white" size={22} />
          </LogIn.Button>
          <LogIn.MenuContainer>
            <Menu
              opener={buttonRef}
              open={openMenu}
              onClose={() => toggle(false)}
            >
              <LogIn.MenuItem className="snipcart-customer-signin">
                <LogInIcon size={16} />
                Log In
              </LogIn.MenuItem>
              <LogIn.MenuItem className="snipcart-customer-register">
                <UserPlusIcon size={16} />
                Register
              </LogIn.MenuItem>
            </Menu>
          </LogIn.MenuContainer>
        </LogIn.Wrapper>
      )}

      {!isMobile && !isLoggedIn && (
        <LogIn.DesktopButton className="snipcart-customer-signin">
          Log In
        </LogIn.DesktopButton>
      )}

      {isLoggedIn && (
        <User.Wrapper>
          <User.Button ref={buttonRef} onClick={toggle}>
            <User.Picture>
              {snipcartState?.customer?.email[0].toUpperCase()}
            </User.Picture>
          </User.Button>
          <User.MenuContainer>
            <Menu
              opener={buttonRef}
              open={openMenu}
              onClose={() => toggle(false)}
            >
              <Menu.Item className="snipcart-customer-signin">Orders</Menu.Item>
              <Menu.Item onClick={snipcartState?.signOut}>Log Out</Menu.Item>
            </Menu>
          </User.MenuContainer>
        </User.Wrapper>
      )}
    </>
  )
}

const LogIn = {
  Wrapper: tw.div`relative rounded-full md:hidden`,
  Button: tw.button`flex items-center justify-center w-8 h-8 text-sm transition duration-150 ease-in-out bg-gray-600 border-2 border-transparent rounded-full focus:outline-none focus:border-white`,
  MenuContainer: tw.div`absolute right-0 z-10 w-48 mt-2 origin-top-right rounded-md`,
  DesktopButton: tw(
    Button
  )`text-white border-white px-6 py-1 hover:bg-white hover:text-black rounded-full hidden sm:block`,
  MenuItem: styled(Menu.Item)`
    ${tw`flex items-center`}

    & > *:first-of-type {
      ${tw`mr-2`}
    }
  `,
}

const User = {
  Wrapper: tw.div`relative`,
  Button: tw.button`flex text-sm transition duration-150 ease-in-out border-2 border-transparent rounded-full focus:outline-none focus:border-white`,
  Picture: tw.div`flex items-center justify-center w-8 h-8 text-lg font-semibold text-white bg-gray-700 rounded-full`,
  MenuContainer: tw.div`absolute right-0 z-10 w-48 mt-2 origin-top-right rounded-md`,
}

interface Props {
  location: Location
}

export const Header: React.FC<Props> = ({ location }) => {
  const data: HeaderQuery = useStaticQuery(graphql`
    query Header {
      logo: file(absolutePath: { regex: "/logo-white.png/" }) {
        childImageSharp {
          fluid {
            ...GatsbyImageSharpFluid_noBase64
          }
        }
      }
      categories: allMarkdownRemark {
        group(field: frontmatter___categories) {
          name: fieldValue
        }
      }
    }
  `)

  const snipcartState = useSnipcart()

  const categories = data.categories.group

  return (
    <>
      <HeaderStyles.Wrapper>
        <HeaderStyles.LogoWrapper>
          <HeaderStyles.MobileNav size={26} />
          <Link to="/">
            <HeaderStyles.Logo
              fluid={data.logo.childImageSharp.fluid}
              alt="MixCart Logo"
            />
          </Link>
        </HeaderStyles.LogoWrapper>
        <HeaderStyles.SearchContainer>
          <Search />
        </HeaderStyles.SearchContainer>
        <Nav.Main>
          <Cart.Button className="snipcart-checkout">
            <Badge badgeContent={snipcartState?.cart?.items.length}>
              <Cart.Icon />
            </Badge>
          </Cart.Button>
          <Authentication />
        </Nav.Main>
      </HeaderStyles.Wrapper>
      <Nav.Banner>
        <Nav.Categories>
          {categories.map(({ name }: { name: string }) => {
            if (name in irregularPluralWords) {
              addIrregularRule(name, name)
            }

            return (
              <Category key={name} to={`/categories/${name}`}>
                {plural(name)}
              </Category>
            )
          })}
          <Category to="/deals">Deals</Category>
        </Nav.Categories>
      </Nav.Banner>
    </>
  )
}

const HeaderStyles = {
  Wrapper: tw.header`flex flex-wrap items-center justify-between p-4 bg-fresh-green`,
  LogoWrapper: tw.div`flex items-center`,
  MobileNav: tw(HamburgerMenu)`text-white mt-1 md:hidden`,
  Logo: tw(Image)`ml-3 w-32 md:ml-0 md:w-40 order-1`,
  SearchContainer: tw.div`order-3 w-full mt-4 md:mt-0 md:flex-1 md:px-24 md:order-2 xl:px-56`,
}

const Nav = {
  Main: styled.nav`
    ${tw`flex items-center order-2 md:order-3`}

    & > *:not(:last-child) {
      ${tw`mr-4`}
    }
  `,
  Banner: tw.nav`hidden pb-2 md:block bg-fresh-green`,
  Categories: styled.div`
    ${tw`flex items-center justify-center`}
    & > *:not(:last-child) {
      ${tw`mr-12`}
    }
  `,
}

const Cart = {
  Icon: tw(
    ShoppingCart
  )`cursor-pointer text-white hover:text-gray-200 transition-colors duration-100 ease-in-out`,
  Button: tw.button`focus:outline-none`,
}

const Category = tw(
  Link
)`text-sm font-semibold text-white capitalize transform hover:scale-105 transition-transform duration-100`
